import { useMemo } from 'react';

import { useMytaverseEvent } from '@core/dashboard/providers';

import {
  isBrandingEntityEditorAvailable,
  resolveBrandingEntityCatalogue,
  type IBrandingEntityCatalogueEntry,
} from './brandingEntities';

export interface UseBrandingEntityCatalogueResult {
  /** Every addressable brandable object for this room, both sources joined. */
  entities: IBrandingEntityCatalogueEntry[];
  /** Whether the objects panel may be offered at all. */
  isAvailable: boolean;
}

/**
 * The brandable objects of the current room, from the build and from Unreal.
 *
 * The class schemas come off the streamed build's own record rather than off the
 * map, for the same reason the room kit does: a schema describes what one build
 * ships, and a map can be served by several builds — see `useMapEditorCatalog`.
 *
 * @returns The joined rows and whether the objects panel is available.
 */
export const useBrandingEntityCatalogue =
  (): UseBrandingEntityCatalogueResult => {
    const {
      currentEvent,
      currentRoom,
      pixelStreamingClient,
      liveBrandingEntities,
    } = useMytaverseEvent();

    const map = currentRoom?.map;
    const brandingTypes = pixelStreamingClient?.clientParams?.brandingTypes;
    const buildId = pixelStreamingClient?.clientId;

    return useMemo(() => {
      const sources = {
        map,
        liveEntities: liveBrandingEntities,
        brandingTypes,
        buildId,
      };

      return {
        entities: resolveBrandingEntityCatalogue(currentEvent, sources),
        isAvailable: isBrandingEntityEditorAvailable(currentEvent, sources),
      };
    }, [currentEvent, map, liveBrandingEntities, brandingTypes, buildId]);
  };
